import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import logoicon from './img/vivosol-favicon.png';
import HeaderNavbar from "./components/layout/HeaderNavbar";
import CopyrightFooter from './components/layout/CopyrightFooter'
import LastUpdatedFooter from './components/layout/LastUpdatedFooter'
import VivosolRoutes from './components/routes/VivosolRoutes'
import { PortfolioDataProvider } from "./components/data/PortfolioData";
import { SessionDataProvider } from "./components/data/SessionData";
import { EnvironmentDataProvider, useEnvironment } from "./components/data/EnvironmentData";
import { AuthProvider } from "./components/auth/AuthProvider";
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { setAccessTokenGetter, setIdTokenGetter } from './api/apiClient';
import { shouldUseAuth0 } from './auth/auth0Config';
import ChatWidget from './components/layout/ChatWidget';

const AUTH0_PUBLIC_PATHS = new Set(['/logout', '/login']);

function isAuth0PublicPath(pathname = window.location.pathname) {
  return AUTH0_PUBLIC_PATHS.has(String(pathname).toLowerCase());
}

function Layout () {
  const location = useLocation();
  const { isDev, environment } = useEnvironment();
  const hideElementsRoutes = ["/login", "/Login", "/", "/logout", "/Logout"];
  const shouldHideElements = hideElementsRoutes.includes(location.pathname);
  const favicon = document.getElementById('favicon');

  if (favicon) {
    favicon.setAttribute('href', logoicon);
  }

  useEffect(() => {
    document.documentElement.dataset.vivosolEnvironment = environment;
    document.documentElement.dataset.vivosolIsDev = isDev ? 'true' : 'false';
  }, [environment, isDev]);
  
  return (
    <>
      <AuthProvider>
        {!shouldHideElements && <HeaderNavbar />}
        <SessionDataProvider>
          <PortfolioDataProvider>
            <VivosolRoutes />
            {!shouldHideElements && <LastUpdatedFooter />}
            {!shouldHideElements && <CopyrightFooter />}
            {!shouldHideElements && <ChatWidget />}
          </PortfolioDataProvider>
        </SessionDataProvider>
      </AuthProvider>
    </>
  );
}

function AppShell() {
  return (
    <Router>
      <EnvironmentDataProvider>
        <Layout />
      </EnvironmentDataProvider>
    </Router>
  );
}

function Auth0AccessTokenBridge({ children }) {
  const { getAccessTokenSilently, getIdTokenClaims, isAuthenticated } = useAuth0();
  const [isTokenReady, setIsTokenReady] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setAccessTokenGetter(null);
      setIdTokenGetter(null);
      setIsTokenReady(false);
      return undefined;
    }

    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    setAccessTokenGetter(() =>
      getAccessTokenSilently(
        audience ? { authorizationParams: { audience } } : undefined
      )
    );
    setIdTokenGetter(async () => {
      const claims = await getIdTokenClaims();
      return claims?.__raw ?? null;
    });
    setIsTokenReady(true);

    return () => {
      setAccessTokenGetter(null);
      setIdTokenGetter(null);
      setIsTokenReady(false);
    };
  }, [getAccessTokenSilently, getIdTokenClaims, isAuthenticated]);

  if (!isTokenReady) {
    return (
      <div className="content p-5">
        <p>Signing in...</p>
      </div>
    );
  }

  return children;
}

function Auth0Gate({ children }) {
  const { isLoading, isAuthenticated, loginWithRedirect, error } = useAuth0();

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error && !isAuth0PublicPath()) {
      loginWithRedirect();
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, error]);

  if (error) {
    return (
      <div className="content p-5">
        <p>Unable to sign in. {error.message}</p>
      </div>
    );
  }

  if (isLoading || !isAuthenticated) {
    if (isAuth0PublicPath()) {
      return children;
    }

    return (
      <div className="content p-5">
        <p>Redirecting to login...</p>
      </div>
    );
  }

  return <Auth0AccessTokenBridge>{children}</Auth0AccessTokenBridge>;
}

function App() {
  if (!shouldUseAuth0()) {
    return <AppShell />;
  }

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email'
      }}
    >
      <Auth0Gate>
        <AppShell />
      </Auth0Gate>
    </Auth0Provider>
  );
}

export default App;
